import React, { useState, useMemo, useEffect } from 'react';
import SelectedDateAvailability from './SelectedDayAvailability';
import Details from './Details';
import { Box, makeStyles } from '@material-ui/core';
import { TIME_AVAILABILITY_TYPES } from '../venueAvailability/timeAvailabilityTypes';
import { WIDTH_BREAKPOINT } from '../../utils/constants/Breakpoints';
import { useViewport } from '../../utils/useViewport';
import Divider from '../../components/overrides/Divider';
import {
  EVENT_CAROUSEL_TITLE,
  SIGNATURE_COLLECTION_RESTAURANT_TEXT,
  SIGNATURE_COLLECTION_RESTAURANT_TITLE,
  GET_TICKETS_TEXT,
  BOOK_A_TABLE_TEXT,
  GUEST_SELECTOR_DROPDOWN_LABEL,
  INLINE_AVAILABILITY_ERROR_MESSAGE,
} from '../../assets/copy';
import EventsCarousel from './EventsCarousel';
import EventTicketDropdown from './EventTicketDropdown';
import useEventFlag from '../../utils/useEventFlag';
import { useFeatureFlags } from '../../utils/useFeatureFlags';
import CalendarSelector from '../../components/core/CalendarSelector';
import { showDivider } from './helpers';
import SignatureCollectionTextIcon from '../../components/core/SignatureCollection/SignatureCollectionTextIcon';
import Typography from '../../components/overrides/Typography';
import { isVenueAlwaysOn } from '../../utils/eventHelpers';
import { iconLib } from '../../utils/constants/VenueLabels';
import clsx from 'clsx';
import { isVenueDetailsPage } from '../../utils/pageTypeHelper.js';
import { useDispatch, useSelector } from 'react-redux';
import { actionSetShouldShowErrorInPage } from '../../store/Error/ErrorAction.js';
import { trackErrorPageView } from '../../utils/useOneTag.js';
import ErrorPageView from '../../utils/ErrorPageView.js';
import { useCopy } from '../../utils/useCopy.js';

const useStyles = makeStyles((theme) => ({
  desktopDivider: {
    [theme.breakpoints.up('xl')]: {
      marginLeft: theme.spacing(7),
      width: 'calc(100% - 56px)',
    },
  },
  calendarContainer: ({ pageType }) => {
    return {
      paddingLeft: theme.spacing(3),
      paddingTop: theme.spacing(3),
      [theme.breakpoints.up('xl')]: {
        padding: isVenueDetailsPage(pageType)
          ? theme.spacing(7, 0)
          : theme.spacing(7, 7, 0),
      },
    };
  },
  getTicketsHeaderContainer: ({ pageType }) => ({
    paddingLeft: theme.spacing(3),
    paddingTop: theme.spacing(3),
    [theme.breakpoints.up('xl')]: {
      padding: isVenueDetailsPage(pageType)
        ? theme.spacing(7, 7, 0, 0)
        : theme.spacing(7, 7, 0),
    },
  }),
  policy: {
    padding: theme.spacing(3),
    margin: 0,
    [theme.breakpoints.up('xl')]: {
      padding: theme.spacing(4, 7),
      width: '100%',
    },
  },
  hiddenStyles: {
    display: 'inline-block',
    position: 'absolute',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    height: 1,
    width: 1,
    margin: -1,
    padding: 0,
    border: 0,
  },
  eventsCarouselContainer: {
    width: '100%',
    '& *::-webkit-scrollbar': {
      display: 'none',
    },
  },
  content: ({ pageType }) => ({
    padding: isVenueDetailsPage(pageType)
      ? theme.spacing(3, 0)
      : theme.spacing(3, 3, 0),
    margin: 0,
    [theme.breakpoints.up('xl')]: {
      padding: isVenueDetailsPage(pageType)
        ? theme.spacing(4, 0)
        : theme.spacing(4, 7, 0),
      width: '100%',
    },
  }),
  signatureCollectionTextContainer: {
    marginTop: theme.spacing(1),
  },
}));

const DetailedLayout = ({
  list,
  venue,
  guests,
  user,
  time,
  date,
  loadingAvailability,
  eventList,
  pageType = '',
}) => {
  const { width } = useViewport();
  const classes = useStyles({ pageType });
  const dispatch = useDispatch();
  const {
    venueHasEvent: isEvent,
    isEventOnSale,
    isEventNoLongerAvailable,
  } = useEventFlag(venue);
  const eventIsAvailable = useMemo(() => {
    return isEventOnSale && isEvent && !isEventNoLongerAvailable;
  }, [isEvent, isEventNoLongerAvailable, isEventOnSale]);

  const isAlwaysOn = isVenueAlwaysOn(venue);

  const { refreshAvailabilityCalendarFlagIsEnabled: shouldDisplayCalendar, readOnlyModeFlagIsEnabled } =
    useFeatureFlags();
  const [labels] = useState(venue?.labels?.filter((label) => iconLib[label]));
  const { shouldShowErrorInPage } = useSelector((state) => state?.error);
  const showEventTicketDropdown =
    eventIsAvailable && !isAlwaysOn && !readOnlyModeFlagIsEnabled;
  const { getCopy } = useCopy();

  useEffect(() => {
    dispatch(actionSetShouldShowErrorInPage(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shouldShowErrorInPage) {
      trackErrorPageView(
        new ErrorPageView({
          errorTitle: getCopy(
            'AVAILABILITY_ERROR_IN_VENUE_DETAILS_PAGE_TITLE',
            { venueName: venue?.name, venueId: venue?.id }
          ),
          errorMessage: INLINE_AVAILABILITY_ERROR_MESSAGE,
          value: 'AVAILABILITY_ERROR',
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowErrorInPage, venue?.id, venue?.name]);

  const displayAvailabilityCalendar = () => {
    if (
      !readOnlyModeFlagIsEnabled &&
      shouldDisplayCalendar &&
      (!isEvent || isAlwaysOn)
    ) {
      return (
        <div className={clsx(classes.calendarContainer)}>
          <CalendarSelector
            venue={venue}
            guests={guests}
            loadingAvailability={loadingAvailability}
            showBookTableText={true}
            dateSelectionCTA={isEvent ? GET_TICKETS_TEXT : BOOK_A_TABLE_TEXT}
            ticketSelectionLabel={
              isEvent ? 'Quantity' : GUEST_SELECTOR_DROPDOWN_LABEL
            }
            showAvailabilityError={shouldShowErrorInPage}
          />
        </div>
      );
    }
    return <></>;
  };

  const displayAvailabilitySkeletonLoader = () => {
    if (!readOnlyModeFlagIsEnabled && !shouldShowErrorInPage) {
      return (
        <SelectedDateAvailability
          list={list}
          venue={venue}
          guests={guests}
          user={user}
          time={time}
          date={date}
          loadingAvailability={loadingAvailability}
          type={TIME_AVAILABILITY_TYPES.HORIZONTAL_VENUE_DETAILS_PAGE_DESKTOP}
        />
      );
    }
    return <></>;
  };

  return (
    <>
      {venue.isActive && (
        <>
          {venue?.isSignatureCollection && (
            <Box className={classes.content}>
              <SignatureCollectionTextIcon
                text={SIGNATURE_COLLECTION_RESTAURANT_TITLE}
                variant="small2Semibold"
              />
              <Box className={classes.signatureCollectionTextContainer}>
                <Typography variant="medium1Normal">
                  {SIGNATURE_COLLECTION_RESTAURANT_TEXT}
                </Typography>
              </Box>
            </Box>
          )}
          {displayAvailabilityCalendar()}
          {showEventTicketDropdown && (
            <>
              <div class={classes.getTicketsHeaderContainer}>
                <Typography variant="medium1Semibold" component="h2">
                  {GET_TICKETS_TEXT}
                </Typography>
              </div>
              <EventTicketDropdown venue={venue} pageType={pageType} />
            </>
          )}
          {displayAvailabilitySkeletonLoader()}
        </>
      )}
      {eventList && (
        <Box className={classes.eventsCarouselContainer}>
          <EventsCarousel
            venue={venue}
            list={eventList}
            title={EVENT_CAROUSEL_TITLE}
            isEventCarousel={true}
            isEventsDetailPage={true}
          />
          <Divider className={classes.desktopDivider} />
        </Box>
      )}

      {WIDTH_BREAKPOINT < width &&
        showDivider(venue?.atmospheres?.items?.length > 0 || labels.length > 0)}
      <Details venue={venue} />
      {showDivider(venue?.atmospheres?.items?.length > 0 || labels.length > 0)}

      {width < WIDTH_BREAKPOINT && <Divider width="100%" />}
    </>
  );
};

export default DetailedLayout;
